/**
 * Goal strip and goal inspector (docs/ux/project-tab-spec.md §1.5, P4-goals): the goal in one row
 * with the milestone track (done ✓ · current ● with "n of m criteria verified" · next ○), and an
 * inspector with the criteria, the evidence that verifies each one, and the editor. Progress is
 * criteria verified by evidence, never a percentage from task counts.
 */
import { Button } from '@viko/transcript-ui'
import { Plus, Trash2 } from 'lucide-react'
import { useId, useState } from 'react'
import type {
  GoalController,
  GoalDraft,
  GoalEvidenceSources,
  MilestoneView,
} from '../../lib/goals.js'
import { errorText } from '../ui.js'
import { Glyph } from './glyphs.js'

const STATE_WORD = { done: 'Reached', current: 'Current', next: 'Next' } as const

/** The milestone the goal is on now (the first not reached), for the page head. */
export function currentMilestone(milestones: readonly MilestoneView[]): MilestoneView | undefined {
  return milestones.find((m) => m.state === 'current') ?? milestones.find((m) => m.state !== 'done')
}

/**
 * The goal in the project's page head (refinement spec §2, WP-3): the goal, then the current
 * milestone with its verified criteria, each opening the goal inspector. Without a goal, a quiet
 * "Set a goal" link. Progress is criteria verified by evidence, never a percentage.
 */
export function GoalHead({
  goal,
  onInspect,
}: {
  goal: GoalController
  onInspect: (key: string) => void
}) {
  const v = goal.view
  if (!v) {
    // No goal store (locked, or no encrypted account): the project's own goal text, read-only.
    if (goal.disabled || !goal.ready)
      return goal.seed ? (
        <span className="lk-phead__goal is-static" title={goal.seed}>
          {goal.seed}
        </span>
      ) : null
    return (
      <>
        {goal.seed ? (
          <button
            type="button"
            className="lk-phead__goal"
            onClick={() => onInspect('goal:current:edit')}
            title={goal.seed}
          >
            {goal.seed}
          </button>
        ) : null}
        <button
          type="button"
          className="lk-phead__link"
          onClick={() => onInspect('goal:current:edit')}
        >
          Set a goal
        </button>
      </>
    )
  }
  const m = currentMilestone(v.milestones)
  const allDone = v.milestones.length > 0 && !m
  return (
    <>
      <button
        type="button"
        className="lk-phead__goal"
        onClick={() => onInspect('goal:current')}
        title={v.goal.text}
        aria-label={`Goal: ${v.goal.text}`}
      >
        {v.goal.text}
        {v.goal.status !== 'active' ? ` (${v.goal.status})` : ''}
      </button>
      {m || allDone ? (
        <button type="button" className="lk-phead__ms" onClick={() => onInspect('goal:current')}>
          {m ? (
            <>
              <span className="lk-phead__k">Now:</span> {m.title}
              <span className="lk-phead__n">
                {m.total ? `${m.verified} of ${m.total} criteria verified` : 'no criteria yet'}
              </span>
            </>
          ) : (
            'All milestones reached'
          )}
        </button>
      ) : null}
    </>
  )
}

// ---- inspector ----------------------------------------------------------------------------------

export function GoalInspector({
  goal,
  sources,
  editing,
  onInspect,
  onStarter,
}: {
  goal: GoalController
  sources: GoalEvidenceSources & { titles: Map<string, string> }
  editing: boolean
  onInspect: (key: string) => void
  /** Put starter text in the tab's composer ("Ask Viko for milestones"). */
  onStarter?: ((text: string) => void) | undefined
}) {
  const v = goal.view
  const ask =
    onStarter && !goal.disabled && !v?.milestones.length ? (
      <button
        type="button"
        className="lk-link justify-self-start"
        onClick={() => onStarter('Suggest milestones for this goal: ')}
      >
        Ask Viko for milestones
      </button>
    ) : null
  if (editing || !v)
    return (
      <>
        {ask}
        <GoalEditor goal={goal} sources={sources} onDone={() => onInspect('goal:current')} />
      </>
    )
  return (
    <div className="lk-goalv">
      {ask}
      <p className="lk-goalv__text">{v.goal.text}</p>
      <p className="lk-muted">
        {v.total
          ? `${v.verified} of ${v.total} criteria verified by evidence.`
          : 'No criteria yet. Add checkable criteria and link the evidence that proves each one.'}
      </p>
      {v.milestones.map((m) => (
        <section key={m.id} className="lk-goalv__m" aria-label={m.title}>
          <h3>
            <Glyph kind={m.state === 'done' ? 'ok' : m.state === 'current' ? 'run' : 'idle'} />
            {m.title}
            <span className="lk-muted"> {STATE_WORD[m.state]}</span>
          </h3>
          <ul>
            {m.criteria.map((c) => (
              <li key={c.id}>
                <Glyph kind={c.verified ? 'ok' : 'idle'} />
                <span>
                  {c.text}
                  <span className="sr-only">{c.verified ? ', verified' : ', not verified'}</span>
                  {c.evidence.length === 0 ? (
                    <span className="lk-muted"> No evidence linked</span>
                  ) : (
                    c.evidence.map((e) => (
                      <span key={`${e.ref.kind}:${e.ref.id}`} className="lk-goalv__ev">
                        {sources.titles.get(`${e.ref.kind}:${e.ref.id}`) ?? e.ref.id}
                        {e.ok ? '' : ` (${e.why})`}
                      </span>
                    ))
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <Button type="button" size="sm" onClick={() => onInspect('goal:current:edit')}>
        Edit goal
      </Button>
    </div>
  )
}

const toDraft = (goal: GoalController): GoalDraft =>
  goal.view
    ? {
        text: goal.view.goal.text,
        status: goal.view.goal.status,
        milestones: structuredClone(goal.view.goal.milestones),
      }
    : { text: goal.seed, status: 'active', milestones: [] }

function GoalEditor({
  goal,
  sources,
  onDone,
}: {
  goal: GoalController
  sources: GoalEvidenceSources & { titles: Map<string, string> }
  onDone: () => void
}) {
  const [draft, setDraft] = useState<GoalDraft>(() => toDraft(goal))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const id = useId()
  const options: { value: string; label: string }[] = [
    ...sources.tasks.map((t) => ({
      value: `task:${t.id}`,
      label: `Task: ${sources.titles.get(`task:${t.id}`) ?? t.id}`,
    })),
    ...sources.landings.map((l) => ({
      value: `landing:${l.id}`,
      label: `Landing: ${sources.titles.get(`landing:${l.id}`) ?? l.id}`,
    })),
    ...sources.builds.map((b) => ({
      value: `build:${b.buildId}`,
      label: `Build: ${sources.titles.get(`build:${b.buildId}`) ?? b.buildId}`,
    })),
  ]
  const setMs = (
    i: number,
    f: (m: GoalDraft['milestones'][number]) => GoalDraft['milestones'][number],
  ) => setDraft((d) => ({ ...d, milestones: d.milestones.map((m, j) => (j === i ? f(m) : m)) }))
  const valid =
    draft.text.trim().length > 0 &&
    draft.milestones.every((m) => m.title.trim() && m.criteria.every((c) => c.text.trim()))
  const save = async () => {
    setBusy(true)
    setError('')
    try {
      await goal.save({
        ...draft,
        text: draft.text.trim(),
        milestones: draft.milestones.map((m) => ({
          ...m,
          title: m.title.trim(),
          criteria: m.criteria.map((c) => ({ ...c, text: c.text.trim() })),
        })),
      })
      onDone()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <form
      className="lk-goalf"
      onSubmit={(e) => {
        e.preventDefault()
        if (valid && !busy) void save()
      }}
    >
      <label htmlFor={`${id}-t`}>What is this project for?</label>
      <textarea
        id={`${id}-t`}
        className="input"
        rows={4}
        maxLength={4000}
        value={draft.text}
        onChange={(e) => setDraft({ ...draft, text: e.target.value })}
        disabled={goal.disabled}
      />
      <label htmlFor={`${id}-s`}>Status</label>
      <select
        id={`${id}-s`}
        className="input"
        value={draft.status}
        onChange={(e) => setDraft({ ...draft, status: e.target.value as GoalDraft['status'] })}
      >
        <option value="active">Active</option>
        <option value="paused">Paused</option>
        <option value="achieved">Achieved</option>
      </select>
      {draft.milestones.map((m, i) => (
        <fieldset key={m.id} className="lk-goalf__m">
          <legend>Milestone {i + 1}</legend>
          <div className="lk-goalf__row">
            <input
              className="input"
              aria-label={`Milestone ${i + 1} title`}
              placeholder="A usable outcome, e.g. Pilot on Viko and one other project"
              value={m.title}
              onChange={(e) => setMs(i, (x) => ({ ...x, title: e.target.value }))}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              icon={<Trash2 size={14} />}
              onClick={() =>
                setDraft((d) => ({ ...d, milestones: d.milestones.filter((_, j) => j !== i) }))
              }
            >
              Remove milestone {i + 1}
            </Button>
          </div>
          {m.criteria.map((c, k) => (
            <div key={c.id} className="lk-goalf__row">
              <input
                className="input"
                aria-label={`Milestone ${i + 1} criterion ${k + 1}`}
                placeholder="Something you can check, e.g. A landing goes green and auto-lands"
                value={c.text}
                onChange={(e) =>
                  setMs(i, (x) => ({
                    ...x,
                    criteria: x.criteria.map((y, j) =>
                      j === k ? { ...y, text: e.target.value } : y,
                    ),
                  }))
                }
              />
              <select
                className="input"
                aria-label={`Evidence for milestone ${i + 1} criterion ${k + 1}`}
                value={c.evidence[0] ? `${c.evidence[0].kind}:${c.evidence[0].id}` : ''}
                onChange={(e) => {
                  const [kind, ...rest] = e.target.value.split(':')
                  setMs(i, (x) => ({
                    ...x,
                    criteria: x.criteria.map((y, j) =>
                      j === k
                        ? {
                            ...y,
                            evidence: kind ? [{ kind: kind as 'task', id: rest.join(':') }] : [],
                          }
                        : y,
                    ),
                  }))
                }}
              >
                <option value="">No evidence yet</option>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                icon={<Trash2 size={14} />}
                onClick={() =>
                  setMs(i, (x) => ({ ...x, criteria: x.criteria.filter((_, j) => j !== k) }))
                }
              >
                Remove criterion {k + 1}
              </Button>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            icon={<Plus size={14} />}
            onClick={() =>
              setMs(i, (x) => ({
                ...x,
                criteria: [...x.criteria, { id: crypto.randomUUID(), text: '', evidence: [] }],
              }))
            }
          >
            Add criterion
          </Button>
        </fieldset>
      ))}
      <div>
        <Button
          type="button"
          size="sm"
          icon={<Plus size={14} />}
          onClick={() =>
            setDraft((d) => ({
              ...d,
              milestones: [...d.milestones, { id: crypto.randomUUID(), title: '', criteria: [] }],
            }))
          }
        >
          Add milestone
        </Button>
      </div>
      {error ? (
        <p className="lk-goalf__err" role="alert">
          {error}
        </p>
      ) : null}
      <div className="lk-goalf__row">
        <Button type="submit" variant="primary" disabled={!valid || busy || goal.disabled}>
          Save goal
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
